import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { ToastProgrammatic as Toast } from 'buefy';
import { mapActions } from 'vuex';

import {
  downloadOrderItemsReport,
  downloadOrderMembersReport,
  downloadOrdersReport,
  getGroupBuyingReport,
} from '@/api/reports';
import { GET_DEPARTMENT_LIST } from '@/store/setting/constants';
import {
  DEFAULT_PER_PAGE,
  FIRST_PAGE,
  LAST_WRITE_OFF_STATUS_TEXT,
  PER_PAGE_OPTIONS,
  RECEIVING_STATUS_TEXT,
  RECEIVING_STATUS,
  SNACKBAR_TYPES,
} from '@/utils/constants';
import Order from '@/utils/orderUtil';
import { formatPrice } from '@/utils/helper';

export default {
  name: 'StatisticsLists',
  data() {
    return {
      advancedSearchUnits: [],
      columns: [
        {
          field: 'serial',
          label: '人員編號',
          width: 115,
        },
        {
          field: 'account',
          label: '帳號',
          width: 115,
        },
        {
          field: 'name',
          label: '姓名',
          width: 117,
        },
        {
          field: 'department',
          label: '部門',
          width: 115,
        },
        {
          field: 'ext',
          label: '分機/電話',
          width: 115,
        },
        {
          field: 'receivingStatus',
          label: '領取狀態',
          width: 120,
        },
        {
          centered: true,
          field: 'onSiteOrder',
          label: '現場訂購',
          width: 48,
        },
        {
          field: 'total',
          label: '總計金額',
          numeric: true,
          width: 115,
        },
        {
          field: 'unpaid',
          label: '未收款項',
          numeric: true,
          width: 115,
        },
        {
          field: 'outOfSubsidy',
          label: '自付額',
          numeric: true,
          width: 115,
        },
      ],
      exportBtnLoading: false,
      exportOrderBtnLoading: false,
      filter: undefined,
      filterTagItems: [
        {
          label: '已領取',
          value: 2,
        },
        {
          label: '未領取',
          value: 0,
        },
      ],
      groupBuyingInfo: {},
      isFiltered: false,
      loading: false,
      orders: [],
      openedDetails: [],
      openedFilter: false,
      overLengthMap: {
        name: false,
        'store-name': false,
      },
      overlengthObserver: null,
      overlengthSelectors: [
        '#summary #name',
        '#summary #store-name',
      ],
      page: FIRST_PAGE,
      pagination: {},
      perPage: DEFAULT_PER_PAGE,
      receiveTypes: [],
    };
  },
  computed: {
    disableExport() {
      return !this.formattedList?.length || this.isExporting;
    },
    formattedList() {
      return this.orders?.map(({
        email,
        outOfSubsidy,
        receivingStatus,
        receivedAt,
        total,
        unpaid,
        lastOperation,
        operationTime,
        ...props
      }, index) => ({
        email,
        index,
        receiveStatusText: RECEIVING_STATUS_TEXT[receivingStatus],
        receiveTime: this.getReceivingTimeText({
          operation: lastOperation,
          operationTime,
        }),
        total: total ? this.$options.filters.priceFormat(this.$options.filters.commaFormat(total)) : '-',
        unpaid: unpaid ? this.$options.filters.priceFormat(this.$options.filters.commaFormat(unpaid)) : '-',
        outOfSubsidy: outOfSubsidy ? this.$options.filters.priceFormat(this.$options.filters.commaFormat(outOfSubsidy)) : '-',
        receivingStatus,
        receivedAt,
        lastOperation,
        operationTime,
        ...props,
      }));
    },
    isExporting() {
      return this.exportOrderBtnLoading || this.exportBtnLoading;
    },
    orderId() {
      const { id } = this.$route.params;
      return id || '';
    },
    perPageOptions() {
      return PER_PAGE_OPTIONS;
    },
    receivingStatus() {
      return RECEIVING_STATUS;
    },
    receivingStatusText() {
      return RECEIVING_STATUS_TEXT;
    },
    receiveTimes() {
      const result = [];
      const receiveTimes = this.groupBuyingInfo?.receiveTimes || [];
      receiveTimes.forEach(({ startTime, endTime }) => {
        const start = dayjs(startTime);
        const end = dayjs(endTime);
        if (start.isSame(endTime, 'day')) {
          result.push(start.format('MM/DD'));
        } else {
          result.push(`${start.format('MM/DD')}-${end.format('MM/DD')}`);
        }
      });
      return result;
    },
    receiveDropdownItems() {
      const result = [];
      let items = [];
      let count = 0;
      this.receiveTimes.forEach((time) => {
        const length = time.includes('-') ? 2 : 1;
        if (count + length > 5) {
          result.push(items.join('、'));
          items = [];
          count = 0;
        }
        items.push(time);
        count += length;
      });
      if (items.length) {
        result.push(items.join('、'));
      }
      return result;
    },
    startTimeText() {
      return dayjs(this.groupBuyingInfo?.startTime).format('MM/DD');
    },
    statusText() {
      return new Order(this.groupBuyingInfo).cardTypeDescription || '';
    },
    total() {
      return this.pagination?.total || 0;
    },
    userUnits() {
      return this.departments?.map(({ id, name }) => ({ key: id, value: name })) || [];
    },
  },
  async created() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    this.loadData();
  },
  async mounted() {
    this.overlengthObserver = new ResizeObserver((entries) => this.onOverlengthEvent(entries));
    this.registerOverlengthObservers();
    this.departments = await this.getDepartmentList();
  },
  beforeDestroy() {
    if (this.overlengthObserver) {
      this.overlengthObserver.disconnect();
    }
    Object.assign(this.$data, this.$options.data.apply(this));
  },
  watch: {
    page() {
      this.loadData();
    },
    perPage(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.setPageOrGetData();
      }
    },
  },
  methods: {
    ...mapActions('setting', {
      getDepartmentList: GET_DEPARTMENT_LIST,
    }),
    advancedSearchUnitsChange() {
      this.setPageOrGetData();
    },
    columnThAttrs() {
      return {
        class: 'text-xs',
      };
    },
    formatPrice,
    getOrderDetails(items = []) {
      return items?.map((item = {}) => {
        const {
          entree: {
            name,
            options = [],
          } = {},
          ...props
        } = item || [];

        return {
          name,
          options,
          ...props,
        };
      }) || [];
    },
    getParams() {
      return {
        departments: this.advancedSearchUnits.map(({ key }) => key),
        page: this.page,
        per_page: this.perPage,
        receive_types: this.receiveTypes,
        search: this.filter,
      };
    },
    async exportOrdersReport(byMember = true) {
      try {
        const { id } = this.$route.params;
        this.exportOrderBtnLoading = true;
        let res = {};
        if (byMember) {
          res = await downloadOrderMembersReport(id, this.getParams());
        } else {
          res = await downloadOrderItemsReport(id, this.getParams());
        }
        const { content = '', filename } = res || {};
        const blob = new Blob([content], { type: 'text/csv' });

        saveAs(blob, filename);
      } catch (ex) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '下載失敗',
          msg: ex.message || '下載時發生未知錯誤，請再試一次',
        });
      } finally {
        this.exportOrderBtnLoading = false;
      }
    },
    itemRowClass(item) {
      if (this.openedDetails.includes(item.index)) {
        return 'item-table__row item-table__row--opened';
      }
      return 'item-table__row';
    },
    async exportStatisticsReport() {
      const { id } = this.$route.params;
      try {
        this.exportBtnLoading = true;
        const res = await downloadOrdersReport(id);
        const { filename, content = '' } = res || {};
        const blob = new Blob([content], { type: 'text/csv' });

        saveAs(blob, filename);
      } catch (ex) {
        this.$snackbar({
          type: SNACKBAR_TYPES.ERROR,
          title: '下載失敗',
          msg: ex.message || '下載時發生未知錯誤，請再試一次',
        });
      } finally {
        this.exportBtnLoading = false;
      }
    },
    async loadData() {
      const { id } = this.$route.params;

      try {
        this.loading = true;
        const params = this.getParams();
        const {
          data: {
            groupBuyingInfo = {},
            orders = [],
          } = {},
          link: { pagination = {} } = {},
        } = await getGroupBuyingReport(id, params);

        this.groupBuyingInfo = groupBuyingInfo;
        this.orders = orders;
        this.pagination = pagination;
      } catch (ex) {
        const { message = '' } = ex;

        Toast.open({
          message: `讀取報表資料時發生錯誤，請稍後再試（${message}）`,
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },
    async onSearch() {
      if (!this.filter) {
        return;
      }
      try {
        this.isFiltered = true;
        this.setPageOrGetData();
      } catch (ex) {
        this.isFiltered = false;
      }
    },
    getReceivingTimeText({
      operation = 0,
      operationTime,
    }) {
      if (LAST_WRITE_OFF_STATUS_TEXT[operation] && operationTime) {
        const formattedTime = dayjs(operationTime).format('MM/DD HH:mm');
        return ` (${formattedTime} ${LAST_WRITE_OFF_STATUS_TEXT[operation]})`;
      }
      return '';
    },
    async onReset() {
      this.isFiltered = false;
      this.filter = '';
      this.setPageOrGetData();
    },
    onSelectItem({ value }) {
      const index = this.receiveTypes.indexOf(value);
      if (index !== -1) {
        this.receiveTypes.splice(index, 1);
      } else {
        this.receiveTypes.push(value);
      }

      this.setPageOrGetData();
    },
    onOverlengthEvent(entries) {
      entries.forEach((entry) => {
        const { target: { id, offsetWidth = 0, scrollWidth = 0 } = {} } = entry;
        if (id) {
          this.overLengthMap[id] = offsetWidth < scrollWidth;
        }
      });
    },
    setPageOrGetData() {
      this.groupBuyingInfo = {};
      this.orders = [];
      this.pagination = {};
      if (this.page !== FIRST_PAGE) {
        this.page = FIRST_PAGE;
      } else {
        this.loadData();
      }
    },
    registerOverlengthObservers() {
      this.overlengthSelectors.forEach((selector) => {
        const element = document.querySelector(selector);
        if (element) {
          this.overlengthObserver.observe(element);
        }
      });
    },
  },
};
